import { CarDataApiProvider, CarDataApiProviderLabels, type CarFromLicensePlateRecordWithOwners } from '@navio/nsdk';
import { NavioInfoCard, NoddiButton } from 'noddi-ui-web';
import { COUNTRY_OPTIONS, countryCodeToFlag, getLabelMapFromEntries } from 'noddi-util';

/** Refreshing the snapshot. Omitted on read-only surfaces, which then show no action. */
type RegistryRefresh = {
  onRefresh: () => void | Promise<void>;
  isPending: boolean;
  /** False when the car has no plate to look up, which keeps the action visible but disabled. */
  canRefresh: boolean;
};

interface Props {
  /** The card reads the snapshot straight off the record, so no caller restates its fields. */
  car: CarFromLicensePlateRecordWithOwners;
  refresh?: RegistryRefresh;
}

const PROVIDER_LABEL = getLabelMapFromEntries(CarDataApiProvider, CarDataApiProviderLabels);

// Operators think in countries, not provider names, so each register carries the market it covers.
const PROVIDER_COUNTRY: Record<CarDataApiProvider, string> = {
  [CarDataApiProvider.CAR_INFO]: 'DK',
  [CarDataApiProvider.RDW]: 'NL',
  [CarDataApiProvider.STATENS_VEGVESEN]: 'NO'
};

const registrySourceLabel = (provider: CarDataApiProvider) =>
  `${PROVIDER_LABEL[provider]} (${PROVIDER_COUNTRY[provider]})`;

const getCountryDisplay = (code: string | null | undefined): string | undefined => {
  if (code == null) {
    return undefined;
  }
  const known = COUNTRY_OPTIONS.find((option) => option.value === code);
  if (known) {
    return known.label;
  }
  const flag = countryCodeToFlag(code);
  return flag ? `${flag} ${code}` : code;
};

export const ExternalCarRegistryInfoCard = ({ car, refresh }: Props) => {
  // The plate's own country beats the record's, which is where the car was first registered.
  const countryCode = car.licensePlate?.countryCode ?? car.firstRegisteredCountry;
  const registrySource = car.vehicleRegistry;
  return (
    <NavioInfoCard
      title='External info from national car register'
      icon='Database'
      columns={2}
      headerNode={
        refresh && (
          <NoddiButton
            variant='outline'
            size='small'
            startIcon='RefreshCcw'
            loading={refresh.isPending}
            // oxlint-disable-next-line typescript/no-misused-promises -- async onClick runs refresh.onRefresh(); React discards the returned promise, so a rejection is unhandled unless the body catches
            onClick={refresh.onRefresh}
            disabled={!refresh.canRefresh}
          >
            Update from registry
          </NoddiButton>
        )
      }
      items={[
        { label: 'Registry source', value: registrySource ? registrySourceLabel(registrySource) : undefined },
        { label: 'First registered', value: car.firstRegistered, type: 'date' },
        { label: 'First registered in', value: getCountryDisplay(countryCode) },
        { label: 'Previous EU control', value: car.euControlPrevious, type: 'date' },
        { label: 'Next EU control', value: car.euControlNext, type: 'date' },
        { label: 'Deregistered at', value: car.deregisteredAt, type: 'date' },
        { label: 'Updated from vehicle registry', value: car.updatedFromVehicleRegistry, type: 'date' }
      ]}
    />
  );
};
